import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export const useInvoiceFormData = (estimateId: string | null) => {
  // Fetch estimate data with invoicing progress
  const { data: estimate, isLoading: isLoadingEstimate } = useQuery({
    queryKey: ['estimate', estimateId],
    queryFn: async () => {
      if (!estimateId) throw new Error('No estimate ID provided');
      
      const { data, error } = await supabase
        .from('estimates')
        .select(`
          *,
          customers(id, name, last_name)
        `)
        .eq('id', estimateId)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!estimateId,
  });

  // Fetch available credits for the customer
  const { data: customerCredits } = useQuery({
    queryKey: ['customerCreditsForInvoice', estimate?.customer_id],
    queryFn: async () => {
      if (!estimate?.customer_id) return [];
      
      const { data, error } = await supabase
        .from('customer_credits')
        .select('id, credit_amount, remaining_amount, source_type, description')
        .eq('customer_id', estimate.customer_id)
        .eq('status', 'issued')
        .gt('remaining_amount', 0);

      if (error) throw error;
      return data || [];
    },
    enabled: !!estimate?.customer_id,
  });

  const totalAvailableCredit = customerCredits?.reduce((sum, c) => sum + c.remaining_amount, 0) || 0;

  // Fetch estimate line items from main version or fallback to current items
  const { data: lineItems } = useQuery({
    queryKey: ['estimateLineItems', estimateId],
    queryFn: async () => {
      if (!estimateId) throw new Error('No estimate ID provided');
      
      // First get the main version ID from the estimate
      const estimateData = await supabase
        .from('estimates')
        .select('main_version_id')
        .eq('id', estimateId)
        .single();

      const mainVersionId = estimateData.data?.main_version_id;
      
      // Get main version data if it exists
      const { data: mainVersionData, error: mainVersionError } = mainVersionId 
        ? await supabase
            .from('estimate_versions')
            .select('line_items_snapshot')
            .eq('id', mainVersionId)
            .maybeSingle()
        : { data: null, error: null };

      if (mainVersionError) {
        console.error('Error fetching main version:', mainVersionError);
      }

      // If main version exists and has a non-empty line items snapshot, use it
      if (
        mainVersionData?.line_items_snapshot &&
        Array.isArray(mainVersionData.line_items_snapshot) &&
        mainVersionData.line_items_snapshot.length > 0
      ) {
        return mainVersionData.line_items_snapshot.map((item: any, index: number) => {
          const quantity = (item.quantity ?? item.quantity_in_base_units) ?? 0;
          const unitCost = typeof item.unit_cost === 'number'
            ? item.unit_cost
            : (item.unit_cost_snapshot ?? parseFloat(item.unit_cost || '0'));
          const totalCost = typeof item.total_cost === 'number'
            ? item.total_cost
            : (item.total_cost_snapshot ?? parseFloat(item.total_cost || '0'));

        return {
          id: item.id || `snapshot-${index}`,
          estimate_id: estimateId,
          component_type: item.component_type,
          component_id: item.component_id,
          description: item.description,
          base_unit: item.base_unit,
          quantity_in_base_units: quantity,
          unit_cost_snapshot: unitCost,
          total_cost_snapshot: totalCost,
          manual_override: item.manual_override || false,
          manual_unit_cost: item.manual_unit_cost,
          manual_total_cost: item.manual_total_cost,
          sort_order: item.sort_order || index,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
      });
      }

      // Fallback to current estimate line items if no main version
      const { data, error } = await supabase
        .from('estimate_line_items')
        .select('*')
        .eq('estimate_id', estimateId)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      return data || [];
    },
    enabled: !!estimateId,
  });

  // Fetch existing invoices for this estimate
  const { data: existingInvoices } = useQuery({
    queryKey: ['estimateInvoices', estimateId],
    queryFn: async () => {
      if (!estimateId) throw new Error('No estimate ID provided');
      
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('estimate_id', estimateId)
        .neq('status', 'cancelled');

      if (error) throw error;
      return data || [];
    },
    enabled: !!estimateId,
  });

  return {
    estimate,
    lineItems,
    existingInvoices,
    isLoadingEstimate,
    customerCredits,
    totalAvailableCredit,
    hasAvailableCredit: totalAvailableCredit > 0,
  };
};